import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useConfirm } from '../components/ConfirmModal';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import useLimits from '../utils/useLimits';
import TopUpModal from '../components/TopUpModal';
import API from '../utils/api';
import { trackSeriesChapterGenerated, trackSeriesDeleted, trackSeriesViewed } from '../utils/analytics';
import { isViaCoParent } from '../utils/entitlements';

const STATUS_COLOR = { active: '#059669', completed: '#6f42c1', paused: '#d97706' };
const STATUS_BG    = { active: '#f0fdf4', completed: '#f0ebff', paused: '#fffbeb' };

const SeriesDetailPage = () => {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { remaining, refresh: refreshLimits } = useLimits();

  const [series, setSeries]     = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError]     = useState('');
  const [showTopUp, setShowTopUp]   = useState(false);
  const [editMode, setEditMode]     = useState(false);
  const [saving, setSaving]         = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({ title: '', description: '', characters: '', setting: '', status: 'active' });

  const load = useCallback(() => {
    setLoading(true);
    API.get(`/series/${id}`)
      .then(r => {
        setSeries(r.data.series);
        setChapters(r.data.chapters);
        trackSeriesViewed(id);
        const s = r.data.series;
        setEditForm({
          title:       s.title,
          description: s.description || '',
          characters:  (s.characters || []).join(', '),
          setting:     s.setting || '',
          status:      s.status,
        });
      })
      .catch(() => navigate('/dashboard/series'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleGenerateChapter = async (isLastChapter = false) => {
    if (remaining.stories <= 0) { setShowTopUp(true); return; }
    setGenerating(true); setGenError('');
    try {
      const r = await API.post(`/series/${id}/chapter`, { isLastChapter });
      setChapters(prev => [...prev, r.data.story]);
      setSeries(prev => ({
        ...prev,
        chapterCount: r.data.chapterNumber,
        status: isLastChapter ? 'completed' : prev.status,
      }));
      trackSeriesChapterGenerated(r.data.chapterNumber, isLastChapter);
      refreshLimits();
    } catch (err) {
      if (err.response?.data?.limitReached) { setShowTopUp(true); return; }
      setGenError(err.response?.data?.message || 'Chapter generation failed. Please try again.');
    } finally { setGenerating(false); }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const characters = editForm.characters.split(',').map(c => c.trim()).filter(Boolean);
      const r = await API.patch(`/series/${id}`, { ...editForm, characters });
      setSeries(r.data.series);
      setEditMode(false);
    } catch (err) {
      setGenError(err.response?.data?.message || 'Failed to save changes.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete series?', message: `"${series.title}" and all ${series.chapterCount} chapter(s) will be permanently deleted.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    await API.delete(`/series/${id}`);
    trackSeriesDeleted();
    navigate('/dashboard/series');
  };

  if (loading) return (
    <DashboardLayout title="Loading…">
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
      </div>
    </DashboardLayout>
  );

  if (!series) return null;

  const storiesOut = remaining.stories <= 0;
  const isCompleted = series.status === 'completed';

  return (
    <DashboardLayout title={series.title}>
      {/* Breadcrumb */}
      <div className="d-flex align-items-center gap-2 mb-4">
        <Link to="/dashboard/series" style={{ color: '#6f42c1', fontSize: '14px', textDecoration: 'none' }}>
          <i className="bi bi-arrow-left me-1"></i>All Series
        </Link>
        <span className="text-muted">/</span>
        <span style={{ fontSize: '14px', color: 'var(--text-muted2)' }}>{series.title}</span>
      </div>

      <div className="row g-4">
        {/* ── Left: Series info ────────────────────────────────────────────── */}
        <div className="col-lg-4">
          {/* Cover card */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '20px', overflow: 'hidden' }}>
            <div style={{
              height: '180px',
              background: series.coverIllustrationUrl
                ? `url(${series.coverIllustrationUrl}) center/cover`
                : 'linear-gradient(135deg,#2d1b69,#6f42c1)',
              position: 'relative',
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
              <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px' }}>
                <div style={{ color: '#fff', fontFamily: "'Fredoka One',cursive", fontSize: '20px', textShadow: '0 1px 4px rgba(0,0,0,0.5)', lineHeight: 1.3 }}>
                  {series.title}
                </div>
                <span className="badge rounded-pill mt-1 px-2 py-1" style={{ background: STATUS_BG[series.status], color: STATUS_COLOR[series.status], fontSize: '11px' }}>
                  {series.status}
                </span>
              </div>
            </div>

            <div className="card-body p-3">
              {/* Child */}
              <div className="d-flex align-items-center gap-2 mb-3">
                <span style={{ fontSize: '24px' }}>{series.child?.avatar || '🧒'}</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{series.child?.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Age {series.child?.age}</div>
                </div>
              </div>

              {/* Stats row */}
              <div className="row g-2 mb-3">
                {[
                  { label: 'Chapters', value: series.chapterCount },
                  { label: 'Genre',    value: series.genre },
                  { label: 'Language', value: series.language?.toUpperCase() },
                ].map(({ label, value }) => (
                  <div key={label} className="col-4 text-center p-2 rounded-3" style={{ background: 'var(--header-bg)' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-main)' }}>{value}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>{label}</div>
                  </div>
                ))}
              </div>

              {series.description && (
                <p style={{ fontSize: '13px', color: 'var(--text-muted2)', lineHeight: 1.6, marginBottom: '12px' }}>
                  {series.description}
                </p>
              )}

              {series.characters?.length > 0 && (
                <div className="mb-3">
                  <div style={{ fontSize: '11px', color: 'var(--text-soft)', fontWeight: 600, marginBottom: '6px' }}>RECURRING CHARACTERS</div>
                  <div className="d-flex flex-wrap gap-1">
                    {series.characters.map((c, i) => (
                      <span key={i} className="badge rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {series.setting && (
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-soft)', fontWeight: 600, marginBottom: '4px' }}>SETTING</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{series.setting}</div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {!isCompleted && (
            <div className="card border-0 shadow-sm mb-3 p-3" style={{ borderRadius: '16px' }}>
              <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-main)' }}>Generate Next Chapter</div>

              {genError && <div className="alert alert-danger py-2 small mb-2">{genError}</div>}

              {storiesOut ? (
                <div className="alert alert-warning py-2 small mb-2">
                  Monthly story limit reached.{' '}
                  <button className="btn btn-sm btn-warning rounded-pill px-3 ms-1" onClick={() => setShowTopUp(true)}>Buy Story Pack</button>
                </div>
              ) : (
                <div className="text-muted small mb-2">
                  Uses 1 story from your monthly allowance ({remaining.stories} remaining)
                </div>
              )}

              <div className="d-flex gap-2 flex-wrap">
                <button
                  disabled={generating || storiesOut}
                  onClick={() => handleGenerateChapter(false)}
                  className="btn btn-dream rounded-pill fw-semibold flex-fill"
                  style={{ fontSize: '14px' }}>
                  {generating
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</>
                    : <><i className="bi bi-plus-lg me-2"></i>Next Chapter</>}
                </button>
                {series.chapterCount > 0 && (
                  <button
                    disabled={generating || storiesOut}
                    onClick={async () => { const ok = await confirm({ title: 'Generate final chapter?', message: 'This will generate the last chapter and mark the series as complete.', confirmLabel: 'Generate Final' }); if (ok) handleGenerateChapter(true); }}
                    className="btn btn-outline-secondary rounded-pill px-3"
                    style={{ fontSize: '13px' }}
                    title="Generate final chapter and close the series">
                    Final Chapter
                  </button>
                )}
              </div>
            </div>
          )}

          {isCompleted && (
            <div className="alert border-0 p-3 mb-3" style={{ borderRadius: '16px', background: 'var(--purple-section)' }}>
              <div className="fw-semibold" style={{ color: '#6f42c1' }}>✨ Series complete!</div>
              <div className="text-muted small">This {series.chapterCount}-chapter adventure is finished. Start a new series anytime.</div>
            </div>
          )}

          {/* Edit / Delete */}
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary rounded-pill flex-fill" style={{ fontSize: '13px' }}
              onClick={() => setEditMode(m => !m)}>
              <i className="bi bi-pencil me-1"></i>{editMode ? 'Cancel Edit' : 'Edit Series'}
            </button>
            {!isViaCoParent(user) && (
            <button className="btn btn-outline-danger rounded-pill px-3" style={{ fontSize: '13px' }} onClick={handleDelete}>
              <i className="bi bi-trash"></i>
            </button>
            )}
          </div>

          {/* Edit form */}
          {editMode && (
            <div className="card border-0 shadow-sm mt-3 p-3" style={{ borderRadius: '16px' }}>
              <form onSubmit={handleSaveEdit}>
                <div className="mb-2">
                  <label className="form-label small fw-semibold">Series Title <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" style={{ borderRadius: '8px' }}
                    placeholder='e.g. "The Adventures of Luna"'
                    value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} required />
                  <div className="form-text" style={{ fontSize: '11px' }}>Used as the series name in every chapter heading.</div>
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-semibold">Premise <span className="text-muted fw-normal">(optional)</span></label>
                  <textarea className="form-control form-control-sm" rows={2} style={{ borderRadius: '8px', resize: 'none' }}
                    placeholder='e.g. "Luna is a brave girl who explores a magical forest kingdom with her dragon friend Sparks — solving mysteries and making unlikely friends."'
                    value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                  <div className="form-text" style={{ fontSize: '11px' }}>This is used as the story's backbone for every chapter that gets generated.</div>
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-semibold">Recurring Characters <span className="text-muted fw-normal">(comma-separated, optional)</span></label>
                  <input className="form-control form-control-sm" style={{ borderRadius: '8px' }}
                    placeholder='e.g. "Luna the brave explorer, Sparks the clumsy dragon, Elder Oak the wise talking tree"'
                    value={editForm.characters} onChange={e => setEditForm(f => ({ ...f, characters: e.target.value }))} />
                  <div className="form-text" style={{ fontSize: '11px' }}>These characters appear in every chapter. Describe them briefly so they stay consistent throughout the series.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">World / Setting <span className="text-muted fw-normal">(optional)</span></label>
                  <input className="form-control form-control-sm" style={{ borderRadius: '8px' }}
                    placeholder='e.g. "A magical forest kingdom with hidden waterfalls, talking animals, and ancient ruins"'
                    value={editForm.setting} onChange={e => setEditForm(f => ({ ...f, setting: e.target.value }))} />
                  <div className="form-text" style={{ fontSize: '11px' }}>Grounds every chapter in the same world. The more vivid, the more consistent the imagery.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Status</label>
                  <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                    value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="active">Active — still generating chapters</option>
                    <option value="paused">Paused — taking a break</option>
                    <option value="completed">Completed — story is finished</option>
                  </select>
                </div>
                <button type="submit" disabled={saving} className="btn btn-dream btn-sm rounded-pill w-100">
                  {saving ? <span className="spinner-border spinner-border-sm"></span> : 'Save Changes'}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* ── Right: Chapter list ──────────────────────────────────────────── */}
        <div className="col-lg-8">
          <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            Chapters ({chapters.length})
          </h5>

          {chapters.length === 0 ? (
            <div className="card border-0 shadow-sm text-center py-5 px-4" style={{ borderRadius: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📖</div>
              <p className="fw-semibold mb-1" style={{ color: 'var(--text-main)' }}>No chapters yet</p>
              <p className="text-muted small mb-3">Generate the first chapter to start the adventure!</p>
              <button disabled={generating || storiesOut} onClick={() => handleGenerateChapter(false)}
                className="btn btn-dream rounded-pill px-5 mx-auto fw-semibold">
                {generating ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</> : '✨ Generate Chapter 1'}
              </button>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {chapters.map((ch, idx) => (
                <Link key={ch._id} to={`/dashboard/story/${ch._id}`} style={{ textDecoration: 'none' }}>
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', transition: 'transform 0.12s, box-shadow 0.12s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(111,66,193,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                    <div className="card-body p-3 d-flex gap-3 align-items-start">
                      {/* Chapter number badge */}
                      <div className="d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0 rounded-3"
                        style={{ width: '44px', height: '44px', background: 'linear-gradient(135deg,#6f42c1,#9b59b6)', fontSize: '16px' }}>
                        {ch.chapterNumber}
                      </div>

                      {/* Chapter illustration thumbnail */}
                      {ch.illustrationUrl && (
                        <img src={ch.illustrationUrl} alt="" className="rounded-3 flex-shrink-0"
                          style={{ width: '64px', height: '64px', objectFit: 'cover' }} />
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '2px' }}>
                          {ch.chapterTitle || `Chapter ${ch.chapterNumber}`}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginBottom: '6px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ch.title}
                        </div>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                          {ch.readingTime && (
                            <span style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                              <i className="bi bi-clock me-1"></i>{ch.readingTime} min
                            </span>
                          )}
                          {ch.readCount > 0 && (
                            <span style={{ fontSize: '12px', color: 'var(--text-soft)' }}>
                              <i className="bi bi-eye me-1"></i>{ch.readCount}x read
                            </span>
                          )}
                          {ch.isFavorite && (
                            <span style={{ fontSize: '12px', color: '#f59e0b' }}>
                              <i className="bi bi-star-fill"></i>
                            </span>
                          )}
                          {(ch.illustrationStatus === 'done' || ch.illustrationStatus === 'done_no_face') && (
                            <span className="badge rounded-pill" style={{ background: '#f0fdf4', color: '#059669', fontSize: '10px' }}>
                              🎨 Illustrated
                            </span>
                          )}
                          {ch.illustrationStatus === 'generating' && (
                            <span className="badge rounded-pill" style={{ background: '#fffbeb', color: '#d97706', fontSize: '10px' }}>
                              ⏳ Illustrating…
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                        <i className="bi bi-chevron-right text-muted"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}

              {/* Generate next / final chapter CTAs at bottom of list */}
              {!isCompleted && (
                <div className="card border-0 p-4 text-center" style={{ borderRadius: '16px', background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border: '2px dashed #c9a8ff' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>✨</div>
                  <div className="fw-semibold mb-1" style={{ color: 'var(--text-main)' }}>Ready for Chapter {series.chapterCount + 1}?</div>
                  <div className="text-muted small mb-3">Uses 1 story from your monthly allowance</div>
                  {genError && <div className="alert alert-danger py-2 small mb-2">{genError}</div>}
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <button disabled={generating || storiesOut} onClick={() => handleGenerateChapter(false)}
                      className="btn btn-dream rounded-pill fw-semibold px-4">
                      {generating
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</>
                        : <><i className="bi bi-plus-lg me-2"></i>Generate Chapter {series.chapterCount + 1}</>}
                    </button>
                    <button disabled={generating || storiesOut}
                      onClick={async () => { const ok = await confirm({ title: 'Generate final chapter?', message: 'This will generate the last chapter and mark the series as complete.', confirmLabel: 'Generate Final' }); if (ok) handleGenerateChapter(true); }}
                      className="btn btn-outline-secondary rounded-pill px-4" style={{ fontSize: '13px' }}>
                      Final Chapter
                    </button>
                  </div>
                  {storiesOut && (
                    <button className="btn btn-warning rounded-pill px-4 mt-2 fw-semibold" onClick={() => setShowTopUp(true)}>
                      Buy Story Pack to continue
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showTopUp && <TopUpModal packType="stories" onClose={() => setShowTopUp(false)} />}
    </DashboardLayout>
  );
};

export default SeriesDetailPage;
